import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight, BarChart3, Brain, MessageCircle, Star, Users } from "lucide-react";
import { useNavigateWithScroll } from "../../hooks/useNavigateWithScroll";

export function BusinessSection(props: { showButton?: true }){
    const { t, i18n } = useTranslation();
    const navigate = useNavigateWithScroll()

    return (
        <section id="business" className="py-20 bg-gray-50 dark:bg-gray-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                        {t('business.title')}
                    </h2>
                    <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                        {t('business.subtitle')}
                    </p>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="order-2 lg:order-1"
                    >
                        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
                            <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4">
                                <h3 className="text-white font-semibold">Dashboard 3wChat Business</h3>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-2 gap-4 mb-6">
                                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">Conversations</p>
                                                <p className="text-2xl font-bold text-purple-600">2,847</p>
                                            </div>
                                            <BarChart3 className="w-8 h-8 text-purple-600" />
                                        </div>
                                    </div>
                                    <div className="bg-pink-50 dark:bg-pink-900/20 rounded-lg p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">Utilisateurs</p>
                                                <p className="text-2xl font-bold text-pink-600">1,923</p>
                                            </div>
                                            <Users className="w-8 h-8 text-pink-600" />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Satisfaction moyenne</span>
                                        <div className="flex items-center space-x-1">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <Star key={star} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                            ))}
                                            <span className="text-sm font-semibold ml-2">4.8/5</span>
                                        </div>
                                    </div>
                                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Top requêtes cette semaine</p>
                                        <div className="space-y-1">
                                            <div className="flex justify-between text-xs">
                                                <span className="text-gray-800 dark:text-gray-200">"Comment ça marche ?"</span>
                                                <span className="text-purple-600">23%</span>
                                            </div>
                                            <div className="flex justify-between text-xs">
                                                <span className="text-gray-800 dark:text-gray-200">"Tarifs et abonnements"</span>
                                                <span className="text-purple-600">18%</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        viewport={{ once: true }}
                        className="order-1 lg:order-2"
                    >
                        <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                            {t('business.description')}
                        </p>

                        <div className="space-y-6">
                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <BarChart3 className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                        {t('business.features.analytics')}
                                    </h4>
                                    <p className="text-gray-600 dark:text-gray-300">
                                        Visualisez les métriques clés de l'engagement utilisateur en temps réel
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Brain className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                        {t('business.features.insights')}
                                    </h4>
                                    <p className="text-gray-600 dark:text-gray-300">
                                        Comprenez mieux vos utilisateurs grâce à l'analyse comportementale avancée
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <MessageCircle className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                        {t('business.features.history')}
                                    </h4>
                                    <p className="text-gray-600 dark:text-gray-300">
                                        Accédez à l'historique complet des conversations pour améliorer votre contenu
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
            <br />
            {props.showButton && (<center>
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                        navigate("/business")
                    }}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-2xl transition-all flex items-center justify-center"
                >
                    {/* {t('hero.cta')} */}
                    Parcourir
                    <ArrowRight className="ml-2 w-5 h-5" />
                </motion.button>
            </center>)}
        </section>
    );
};